import React, { ReactNode, useMemo, useRef } from 'react';
import { FocusScope } from 'react-aria';
import { useIsomorphicLayoutEffect } from '@direct-frontend/stdlib';

import { useLayerContext } from '../../layerManager';
import { useIsTopMostItem } from '../../../../hooks/useOverlay/useOverlay';
import { TriggerFirstElementFocus } from '../TriggerFirstElementFocus/TriggerFirstElementFocus';

/**
 * В эффекте вызывает фокус на первый элемент оверлея, при изменении isTopMostWithContainFocus
 *
 * @returns JSX заглушка
 */
export const LayerFocusScope = function LayerFocusScope({
    autoFocusProps,
    contextPopupId,
    isOpen,
    children,
}: {
    autoFocusProps: {
        contain?: boolean;
        restoreFocus?: boolean;
        autoFocus?: boolean;
    };
    isOpen?: boolean;
    contextPopupId?: string;
    children: ReactNode;
}) {
    const layerFocusScopeStartRef = useRef<HTMLElement>(null);
    const layerFocusScopeEndRef = useRef<HTMLElement>(null);

    const nodesRef = useRef<Element[]>([]);
    // Синхронно складываем элементы, находящиеся внутри FocusScope,
    // необходимо для определения находится ли сейчас фокус в FocusScope
    // Делаем на каждый рендер
    useIsomorphicLayoutEffect(() => {
        let node = layerFocusScopeStartRef.current?.nextSibling;
        const nodes: Element[] = [];

        while (node && node !== layerFocusScopeEndRef.current) {
            if (node instanceof Element) {
                nodes.push(node);
            }

            node = node.nextSibling;
        }

        nodesRef.current = nodes;
    }, [children]);

    const layerManager = useLayerContext();
    const itemId = contextPopupId ?? layerManager.id;
    const isTopMostItem = useIsTopMostItem({
        id: itemId,
        isOpen,
    });

    const focusScopeProps = useMemo(() => {
        return {
            contain: Boolean(autoFocusProps.contain && isTopMostItem),
            autoFocus: false,
            restoreFocus: autoFocusProps.restoreFocus,
        };
    }, [autoFocusProps, isTopMostItem]);

    const shouldFocusFirstElement = Boolean(autoFocusProps.autoFocus && isTopMostItem);

    return (
        <FocusScope {...focusScopeProps}>
            <span hidden ref={layerFocusScopeStartRef} />
            {/* Триггерим фокус на первом элементе текущего слоя, если shouldFocusFirstElement=true */}
            <TriggerFirstElementFocus
                autoFocus={Boolean(autoFocusProps.autoFocus)}
                nodesRef={nodesRef}
                shouldFocusFirstElement={shouldFocusFirstElement}
            />
            {children}
            <span hidden ref={layerFocusScopeEndRef} />
        </FocusScope>
    );
};
